import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, Clock, CheckCircle, Truck, ArrowRight, Loader2, XCircle, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDivisasPage = () => {
  const { getAuthHeaders } = useAuth();
  const [ops, setOps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/admin/divisa-operations${params}`, { headers: getAuthHeaders() });
      setOps(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const approveDivisa = async (id) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/divisa-operations/${id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Operacion aprobada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setActionLoading(null); }
  };

  const rejectDivisa = async (id) => {
    if (!window.confirm('Rechazar esta operacion? Se reembolsara el monto al wallet del cliente.')) return;
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/divisa-operations/${id}/reject`, {}, { headers: getAuthHeaders() });
      toast.success('Operacion rechazada y reembolsada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setActionLoading(null); }
  };

  const updateStatus = async (id, status) => {
    setActionLoading(id);
    try {
      await axios.put(`${API}/admin/divisa-operations/${id}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success(`Estado actualizado a ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setActionLoading(null); }
  };

  const statusConfig = {
    pending_approval: { icon: ShieldCheck, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Por Aprobar', next: null },
    processing: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Procesando', next: 'sent' },
    sent: { icon: Truck, color: 'text-indigo-500', bg: 'bg-indigo-50', label: 'Enviado', next: 'completed' },
    completed: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', label: 'Completado', next: null },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', label: 'Rechazada', next: null },
  };

  const filters = [
    { value: 'all', label: 'Todas' },
    { value: 'pending_approval', label: 'Por Aprobar' },
    { value: 'processing', label: 'Procesando' },
    { value: 'sent', label: 'Enviadas' },
    { value: 'completed', label: 'Completadas' },
    { value: 'rejected', label: 'Rechazadas' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  const totalVolume = ops.reduce((s, o) => s + (o.send_amount || 0), 0);
  const totalComm = ops.reduce((s, o) => s + (o.commission_amount || 0), 0);
  const totalProfit = ops.reduce((s, o) => s + (o.profit_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-divisas-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Operaciones de Divisas</h1>
        <p className="text-slate-500">Gestiona las operaciones de cambio (pago por wallet)</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 border-0 shadow-sm text-center">
          <p className="text-sm text-slate-500">Volumen</p>
          <p className="text-xl font-bold">${totalVolume.toFixed(2)}</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm text-center">
          <p className="text-sm text-slate-500">Comisiones</p>
          <p className="text-xl font-bold text-blue-600">${totalComm.toFixed(2)}</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm text-center">
          <p className="text-sm text-slate-500">Ganancias</p>
          <p className="text-xl font-bold text-green-600">${totalProfit.toFixed(2)}</p>
        </Card>
      </div>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <Button key={f.value} variant={filter === f.value ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f.value)}
            className={filter === f.value ? 'bg-blue-700' : ''} data-testid={`filter-${f.value}`}>
            {f.label}
          </Button>
        ))}
      </div>

      {ops.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <RefreshCw className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Sin operaciones</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {ops.map((o) => {
            const sc = statusConfig[o.status] || statusConfig.pending_approval;
            const Icon = sc.icon;
            return (
              <Card key={o.id} className="p-4 border-0 shadow-sm" data-testid={`divisa-${o.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${sc.color}`}><Icon className="w-5 h-5" /></div>
                    <div>
                      <p className="font-semibold text-slate-900">{o.from_currency} → {o.to_currency}</p>
                      <p className="text-sm text-slate-600">Cliente: {o.client_name}</p>
                      {o.recipient_name && <p className="text-sm text-slate-500">Para: {o.recipient_name}</p>}
                      {o.recipient_details && <p className="text-xs text-slate-400">{o.recipient_details}</p>}
                      {o.wallet_deducted && <p className="text-xs text-emerald-600 mt-1">Pagado con Wallet</p>}
                      <p className="text-xs text-slate-400 mt-1">{new Date(o.created_at).toLocaleString('es-ES')}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Envio: <strong>${o.send_amount?.toFixed(2)}</strong></p>
                    <p className="font-bold text-blue-700">{o.receive_amount?.toFixed(2)} {o.to_currency}</p>
                    <div className="text-xs text-slate-500 mt-1">
                      Com: ${o.commission_amount?.toFixed(2)} | Gan: ${o.profit_amount?.toFixed(2)}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${sc.bg} ${sc.color} mt-1 inline-block`}>{sc.label}</span>
                    {/* Approve/Reject for pending_approval */}
                    {o.status === 'pending_approval' && (
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" onClick={() => approveDivisa(o.id)} disabled={actionLoading === o.id} className="bg-green-600 hover:bg-green-700 text-xs" data-testid={`approve-divisa-${o.id}`}>
                          {actionLoading === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle className="w-3 h-3 mr-1" /> Aprobar</>}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectDivisa(o.id)} disabled={actionLoading === o.id} className="text-red-600 border-red-200 text-xs" data-testid={`reject-divisa-${o.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Rechazar
                        </Button>
                      </div>
                    )}
                    {/* Advance status for processing/sent */}
                    {sc.next && (
                      <div className="mt-2">
                        <Button size="sm" onClick={() => updateStatus(o.id, sc.next)} disabled={actionLoading === o.id} className="bg-blue-600 hover:bg-blue-700 text-xs" data-testid={`advance-${o.id}`}>
                          {actionLoading === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ArrowRight className="w-3 h-3 mr-1" /> {statusConfig[sc.next]?.label}</>}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDivisasPage;
